import {Box, Stack} from "@mui/material";
import {CrossSaleAppKey, CrossSaleAppKeyValue} from "crqlar-react-common/dist/models/app/crossSaleOpportunityApps";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";

/*
    Illustrative stand-in for a module the organization is not subscribed to.

    Deliberately static: it renders the module's shape (its real tab names, KPI tiles, a list or a
    calendar grid) from placeholder bars only. No module component is mounted and no request is made,
    so there is no organization data behind the blur - the blur is cosmetic, not the security boundary.
    It holds no focusable element; the page additionally marks it aria-hidden and pointer-events: none.
 */

type MockVariant = "list" | "cards" | "calendar";

const MOCK_LAYOUTS: Record<CrossSaleAppKeyValue, {tabs: string[]; variant: MockVariant}> = {
    [CrossSaleAppKey.CRM]: {tabs: ["Daily List", "Guest list", "Insights", "Demographics"], variant: "list"},
    [CrossSaleAppKey.MARKETING]: {tabs: ["Audiences", "Newsletter", "Lifecycle mails", "Automations"], variant: "cards"},
    [CrossSaleAppKey.SURVEYS]: {tabs: ["Overview", "Surveys", "Automations", "Submissions", "Invitations"], variant: "list"},
    [CrossSaleAppKey.SPA]: {tabs: [], variant: "calendar"},
    [CrossSaleAppKey.RESTAURANT]: {tabs: [], variant: "calendar"},
    [CrossSaleAppKey.WEEKLY_PLANNER]: {tabs: [], variant: "calendar"},
    [CrossSaleAppKey.VOICE_NOTE]: {tabs: [], variant: "list"},
    [CrossSaleAppKey.BOOKING_ENGINE]: {tabs: ["Booking engines"], variant: "cards"},
};

// Fixed "random" widths so the mock looks organic but renders identically every time.
const WIDTHS = [72, 48, 86, 60, 38, 92, 54, 66, 80, 44];
const width = (i: number) => `${WIDTHS[i % WIDTHS.length]}%`;

const Bar = ({w, h = 10, strong = false}: {w: string | number; h?: number; strong?: boolean}) => (
    <Box sx={{width: w, height: h, borderRadius: 999, bgcolor: strong ? "grey.400" : "grey.200"}}/>
);

const Tile = () => (
    <Box sx={{flex: 1, minWidth: 140, p: 2, border: 1, borderColor: "divider", borderRadius: 3}}>
        <Stack gap={1.5}>
            <Bar w="45%"/>
            <Bar w="30%" h={22} strong/>
            <Bar w="60%" h={8}/>
        </Stack>
    </Box>
);

const ListMock = () => (
    <Stack gap={2}>
        <Stack direction="row" gap={2} flexWrap="wrap">
            {[0, 1, 2, 3].map(i => <Tile key={i}/>)}
        </Stack>
        <Box sx={{border: 1, borderColor: "divider", borderRadius: 3, overflow: "hidden"}}>
            {Array.from({length: 10}, (_, row) => (
                <Stack
                    key={row}
                    direction="row"
                    alignItems="center"
                    gap={2}
                    sx={{px: 2, py: 1.75, borderTop: row ? 1 : 0, borderColor: "divider", bgcolor: row ? undefined : "grey.50"}}
                >
                    <Box sx={{width: 28, height: 28, borderRadius: "50%", bgcolor: "grey.200", flexShrink: 0}}/>
                    {[0, 1, 2, 3].map(col => (
                        <Box key={col} sx={{flex: col === 0 ? 2 : 1, display: {xs: col > 1 ? "none" : "block", md: "block"}}}>
                            <Bar w={width(row + col * 3)} strong={row === 0}/>
                        </Box>
                    ))}
                </Stack>
            ))}
        </Box>
    </Stack>
);

const CardsMock = () => (
    <Stack gap={2}>
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: {xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)"},
                gap: 2,
            }}
        >
            {Array.from({length: 6}, (_, i) => (
                <Box key={i} sx={{p: 2, border: 1, borderColor: "divider", borderRadius: 3}}>
                    <Stack gap={1.5}>
                        <Stack direction="row" gap={1.5} alignItems="center">
                            <Box sx={{width: 36, height: 36, borderRadius: 2, bgcolor: "primary.light", opacity: 0.4}}/>
                            <Bar w={width(i)} strong/>
                        </Stack>
                        <Bar w="90%" h={8}/>
                        <Bar w={width(i + 4)} h={8}/>
                        <Stack direction="row" gap={1}>
                            <Box sx={{width: 64, height: 22, borderRadius: 999, bgcolor: "grey.200"}}/>
                            <Box sx={{width: 48, height: 22, borderRadius: 999, bgcolor: "grey.200"}}/>
                        </Stack>
                    </Stack>
                </Box>
            ))}
        </Box>
    </Stack>
);

const CALENDAR_EVENTS = [
    {day: 0, start: 1, span: 2}, {day: 1, start: 3, span: 3}, {day: 2, start: 0, span: 2},
    {day: 2, start: 5, span: 2}, {day: 3, start: 2, span: 4}, {day: 4, start: 1, span: 2},
    {day: 4, start: 6, span: 1}, {day: 5, start: 3, span: 2}, {day: 6, start: 0, span: 3},
];

const CalendarMock = () => (
    <Stack gap={2}>
        <Stack direction="row" gap={1.5} alignItems="center">
            <Box sx={{width: 32, height: 32, borderRadius: 2, bgcolor: "grey.200"}}/>
            <Box sx={{width: 32, height: 32, borderRadius: 2, bgcolor: "grey.200"}}/>
            <Bar w={160} h={14} strong/>
            <Box sx={{flex: 1}}/>
            <Box sx={{width: 180, height: 32, borderRadius: 2, bgcolor: "grey.200", display: {xs: "none", sm: "block"}}}/>
        </Stack>
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                gridTemplateRows: "32px repeat(8, 44px)",
                border: 1,
                borderColor: "divider",
                borderRadius: 3,
                overflow: "hidden",
            }}
        >
            {Array.from({length: 7}, (_, day) => (
                <Box key={`h${day}`} sx={{gridColumn: day + 1, gridRow: 1, p: 1, borderLeft: day ? 1 : 0, borderColor: "divider", bgcolor: "grey.50"}}>
                    <Bar w="60%" strong/>
                </Box>
            ))}
            {Array.from({length: 7 * 8}, (_, i) => (
                <Box
                    key={`c${i}`}
                    sx={{
                        gridColumn: (i % 7) + 1,
                        gridRow: Math.floor(i / 7) + 2,
                        borderLeft: i % 7 ? 1 : 0,
                        borderTop: 1,
                        borderColor: "divider",
                    }}
                />
            ))}
            {CALENDAR_EVENTS.map((event, i) => (
                <Box
                    key={`e${i}`}
                    sx={{
                        gridColumn: event.day + 1,
                        gridRow: `${event.start + 2} / span ${event.span}`,
                        m: 0.5,
                        p: 1,
                        borderRadius: 2,
                        bgcolor: "primary.light",
                        opacity: 0.55,
                    }}
                >
                    <Bar w="70%" h={8} strong/>
                </Box>
            ))}
        </Box>
    </Stack>
);

const CrossSalePreviewMock = ({appKey}: {appKey: CrossSaleAppKeyValue}) => {
    const {t} = useLocalize();
    const {tabs, variant} = MOCK_LAYOUTS[appKey];

    return (
        <Stack gap={2} sx={{p: {xs: 2, md: 3}, width: "100%", boxSizing: "border-box"}}>
            {tabs.length > 0 && (
                <Stack direction="row" gap={3} sx={{borderBottom: 1, borderColor: "divider", overflow: "hidden"}}>
                    {tabs.map((tab, i) => (
                        <Box
                            key={tab}
                            sx={{
                                pb: 1,
                                fontSize: 14,
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                color: i === 0 ? "primary.main" : "text.secondary",
                                borderBottom: i === 0 ? 2 : 0,
                                borderColor: "primary.main",
                            }}
                        >
                            {t(tab)}
                        </Box>
                    ))}
                </Stack>
            )}
            {variant === "list" && <ListMock/>}
            {variant === "cards" && <CardsMock/>}
            {variant === "calendar" && <CalendarMock/>}
        </Stack>
    );
};

export default CrossSalePreviewMock;
